import appApi from "@/app/api";
import { TBillingInterval, TCloudAPIResponse } from "@/types/app";
import { applyLocalizedAmount } from "@/utils/applyLocalizedAmount";
import {
  CHECKOUT_SERVICE_BASE_URL,
  CHECKOUT_V2_ENDPOINTS,
} from "../constants.checkout";
import { CheckoutProduct, TCheckoutType } from "../types.checkout";

export interface CheckoutSummaryPayload {
  products: CheckoutProduct[];
  interval: TBillingInterval;
  discounts?: string[];
}

export type TReferralCredits = {
  amount_off: number; // claimable credit
  total_credits: number;
  label: string; // for display
};

export interface TCheckoutSummaryData {
  // The currency every amount below is in; absent means USD cents. Pass it to
  // `formatMinorAmount`, which picks the divisor off it.
  currency?: string;

  total: number; // total is the price after coupon discount, before customer balance credits, in cents (Stripe Total). Equals sub_total when no Stripe coupon was applied (manual-credit path).
  sub_total: number; // sub_total is the full V2 prorated price before any discount, in cents (Stripe SubTotal).
  tax: number;
  sub_total_excluding_tax: number; // Subtotal before tax, in cents.

  amount_due: number; // Net amount the user has to pay now, in cents. Already accounts for coupon discounts, Aftershoot credits, and tax.
  amount_due_with_referral_credits: number; // amount_due further reduced by the user's claimable referral points, in cents. Will not go below zero.
  coupon_discount_applied: number; // Total coupon discount applied, in cents. This is the sum of all valid applied coupon discounts.
  aftershoot_credits_applied: number; // Aftershoot credits (Stripe customer balance) applied to this purchase, in cents. Always a positive value.

  interval: string;
  discounts:
    | [
        {
          coupon_code: string;
          percent_off: number | null;
          is_sticky: boolean;
          amount_off: number | null;
          invalid: boolean;
          invalid_reason: string;
        },
      ]
    | [];

  migration_status: string; // Migration status for v1 users. Empty for v2 users.

  referral_credits?: TReferralCredits; // about credit

  feature_delta: {
    title: string;
    delta: string[];
  }; // Feature comparison between the user's current plan and the requested plan. Only present for v1 migration users.

  next_invoice_at?: Date;
  next_billing_date?: number;
  unused_days?: number;

  /**
   * price in cent
   */
  annual_savings?: number; // price in cent

  tax_amount: number;
  tax_rate_percentage: number;

  totalAvailableCredit: number; // total referral credits the user has, in cents
  remainingCredit: number; // referral credits left after amount_off is applied, in cents

  executable_discount_codes?: {
    code: string;
    label: string;
  }[];

  /** Absent outside a localised market. */
  localized_amount?: {
    currency: string; // "INR";
    symbol: string; //"₹";
    amount_due: number; //4864;
    amount_due_with_referral_credits: number; //4864;
    tax_amount: number; //0
    sub_total_excluding_tax: number; //4864
    sub_total: number; //4864;
    discounts:
      | [
          {
            coupon_code: string;
            percent_off: number | null;
            is_sticky: boolean;
            amount_off: number | null;
            invalid: boolean;
            invalid_reason: string;
          },
        ]
      | [];
    coupon_discount_applied: number;
    aftershoot_credits_applied: number;
    /** `null` when the user has no referral credits. */
    referral_credits: TReferralCredits | null;
    rate: number; //95.88555100631886;
    markup_percent: number; //;
  };
}

interface Option {
  token: string;
  payload: CheckoutSummaryPayload;
  checkoutType?: TCheckoutType;
  isLocalisedPricing?: boolean;
}

type TResponse = TCloudAPIResponse<TCheckoutSummaryData | null>;

export async function getCheckoutSummary(
  option: Option,
): Promise<TCheckoutSummaryData | null> {
  const headers = {
    Authorization: "Bearer " + option.token,
  };

  const apiUrl = option.checkoutType
    ? CHECKOUT_V2_ENDPOINTS[option.checkoutType].summary
    : `${CHECKOUT_SERVICE_BASE_URL}/purchase/summary`;

  try {
    const response = await appApi.post(apiUrl, option.payload, {
      headers,
    });
    const resData = response.data as TResponse;
    const data = resData.data;
    if (!data) return null;

    // Swap before deriving the credits, so they follow the same currency.
    const summary = applyLocalizedAmount(
      data,
      "checkout summary",
      (localized) => ({
        sub_total: localized.sub_total,
        sub_total_excluding_tax: localized.sub_total_excluding_tax,
        amount_due: localized.amount_due,
        amount_due_with_referral_credits:
          localized.amount_due_with_referral_credits,
        tax_amount: localized.tax_amount,
        coupon_discount_applied: localized.coupon_discount_applied,
        aftershoot_credits_applied: localized.aftershoot_credits_applied,
        discounts: localized.discounts ?? [],
        // Dropped rather than left as-is when absent: the USD credits must
        // not survive into a localised summary.
        referral_credits: localized.referral_credits ?? undefined,
      }),
      option.isLocalisedPricing,
    );

    const totalAvailableCredit = summary.referral_credits?.total_credits ?? 0;
    const remainingCredit =
      totalAvailableCredit - (summary.referral_credits?.amount_off ?? 0);

    return { ...summary, totalAvailableCredit, remainingCredit };
  } catch (error) {
    console.error("Error fetching checkout summary:", error);
    throw error;
  }
}
